import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography as MuiTypography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import InfoCard from 'components/InfoCard/InfoCard';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ScheduleFilterValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { formatAmount } from 'helpers/locale';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { ScheduleCardCompactWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import useScheduleCards from './hooks/useScheduleCards';
import { ScheduleCompactInnerStyled, ScheduleTitleStyled } from './ScheduleCards.styled';

type BreakdownItem = { subCategoryName: string; amount: number; count: number };
const renderBreakdownTooltip = (subCategories: BreakdownItem[]): ReactNode =>
  subCategories.length > 0 ? (
    <div>
      {subCategories.map((sub, subIndex) => (
        <div key={`sub-${subIndex}`} style={{ display: 'flex', gap: 12 }}>
          <span>{sub.subCategoryName}</span>
          <span>
            {sub.count} ({formatAmount(sub.amount ?? 0)})
          </span>
        </div>
      ))}
    </div>
  ) : null;

const AGE_RANGE_ENUMS = [
  ScheduleFilterValuesE.LessThan31Days,
  ScheduleFilterValuesE.From31To60Days,
  ScheduleFilterValuesE.From61To90Days,
  ScheduleFilterValuesE.From91To120Days,
  ScheduleFilterValuesE.Over120Days,
];

type CategoryKey = 'schedule' | 'adjustments' | 'ideal';

/** Dollar amount color: negative = green (good), positive = red (bad), zero = inherit */
const getAmountColor = (amount: number): string | undefined => {
  if (amount < 0) return '#4CAF50'; // green
  if (amount > 0) return '#F44336'; // red
  return undefined; // inherit/default for zero
};

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  schedule: 'Current',
  adjustments: 'Adjustments',
  ideal: 'Ideal',
};

const ACCORDION_SX = {
  boxShadow: 'none',
  '&:before': { display: 'none' },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  '&.MuiAccordion-root': {
    backgroundColor: (theme: Theme): string => theme.palette.background.paper,
  },
} as const;

const ACCORDION_SUMMARY_SX = {
  minHeight: 46,
  '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'none',
    '&.Mui-expanded': { transform: 'rotate(180deg)' },
  },
} as const;

const getSubCategoryIdsForCategory = (
  category: CategoryKey,
  adjustmentIds: string[],
  idealIds: string[]
): string[] | null => {
  if (category === 'adjustments' && adjustmentIds.length > 0) return adjustmentIds;
  if (category === 'ideal' && idealIds.length > 0) return idealIds;
  return null;
};

type HoveredCell = { category: CategoryKey; rowIndex: number } | null;

const CombinedScheduleCards = (): JSX.Element => {
  const [hoveredCell, setHoveredCell] = useState<HoveredCell>(null);
  const [isExpanded, setIsExpanded] = useState((): boolean => {
    try {
      const stored = localStorage.getItem(StorageE.ScheduleCardsExpanded);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  useEffect((): void => {
    try {
      localStorage.setItem(StorageE.ScheduleCardsExpanded, String(isExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isExpanded]);
  const { isMobile } = useResponsive();
  const { scheduleReport, isLoadingScheduleReport } = useScheduleCards();
  const { updateSearchParamsArray } = useTickets();
  const { search: locationSearch } = useLocation();
  const searchParams = useMemo(
    (): URLSearchParams => new URLSearchParams(locationSearch),
    [locationSearch]
  );

  const adjustmentSubCategoryIds = useMemo((): string[] => {
    const fromTotal =
      scheduleReport?.totalAdjustedDataBySubCategories?.map((s): string =>
        String(s.subCategoryId)
      ) ?? [];
    if (fromTotal.length > 0) return fromTotal;
    const firstWithData = scheduleReport?.data?.find(
      d => d.adjustedDataBySubCategories && d.adjustedDataBySubCategories.length > 0
    );
    return (
      firstWithData?.adjustedDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? []
    );
  }, [scheduleReport]);

  const idealSubCategoryIds = useMemo((): string[] => {
    const allIds =
      scheduleReport?.totalDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? [];
    const adjustmentSet = new Set(adjustmentSubCategoryIds);
    return allIds.filter((id): boolean => !adjustmentSet.has(id));
  }, [scheduleReport, adjustmentSubCategoryIds]);

  const idealDataByAgeRange = useMemo((): {
    amount: number;
    count: number;
    subCategories: Array<{ subCategoryName: string; amount: number; count: number }>;
  }[] => {
    if (!scheduleReport?.data) return [];
    const idealSet = new Set(idealSubCategoryIds);
    return scheduleReport.data.map(report => {
      const filtered = (report.dataBySubCategories ?? []).filter((s): boolean =>
        idealSet.has(String(s.subCategoryId))
      );
      return {
        amount: filtered.reduce((sum, s) => sum + (s.amount ?? 0), 0),
        count: filtered.reduce((sum, s) => sum + (s.count ?? 0), 0),
        subCategories: filtered.map(s => ({
          subCategoryName: s.subCategoryName,
          amount: s.amount ?? 0,
          count: s.count ?? 0,
        })),
      };
    });
  }, [scheduleReport, idealSubCategoryIds]);

  const idealTotals = useMemo((): {
    amount: number;
    count: number;
    subCategories: Array<{ subCategoryName: string; amount: number; count: number }>;
  } => {
    if (!scheduleReport?.totalDataBySubCategories) {
      return { amount: 0, count: 0, subCategories: [] };
    }
    const idealSet = new Set(idealSubCategoryIds);
    const filtered = scheduleReport.totalDataBySubCategories.filter((s): boolean =>
      idealSet.has(String(s.subCategoryId))
    );
    return {
      amount: filtered.reduce((sum, s) => sum + (s.amount ?? 0), 0),
      count: filtered.reduce((sum, s) => sum + (s.count ?? 0), 0),
      subCategories: filtered.map(s => ({
        subCategoryName: s.subCategoryName,
        amount: s.amount ?? 0,
        count: s.count ?? 0,
      })),
    };
  }, [scheduleReport, idealSubCategoryIds]);

  const activeCategory = useMemo((): CategoryKey | null => {
    const subParam = searchParams.get(TicketsRefinementKeysE.SubCategory);
    const subIds =
      subParam && subParam !== 'All' ? subParam.split(',').map((s): string => s.trim()) : [];
    if (subIds.length === 0) return 'schedule';
    if (
      adjustmentSubCategoryIds.length > 0 &&
      subIds.length === adjustmentSubCategoryIds.length &&
      adjustmentSubCategoryIds.every((id): boolean => subIds.includes(id))
    ) {
      return 'adjustments';
    }
    if (
      idealSubCategoryIds.length > 0 &&
      subIds.length === idealSubCategoryIds.length &&
      idealSubCategoryIds.every((id): boolean => subIds.includes(id))
    ) {
      return 'ideal';
    }
    return null;
  }, [searchParams, adjustmentSubCategoryIds, idealSubCategoryIds]);

  const activeScheduleRanges = useMemo((): string[] => {
    const scheduleVal = searchParams.get(TicketsRefinementKeysE.Schedule);
    return scheduleVal
      ? scheduleVal
          .split(',')
          .map((v): string => v.trim())
          .filter(Boolean)
      : [];
  }, [searchParams]);

  const isCategoryHighlighted = (category: CategoryKey): boolean =>
    activeCategory === category || (activeCategory === null && category === 'schedule');

  const isRangeCellHighlighted = (category: CategoryKey, rowIndex: number): boolean => {
    if (!isCategoryHighlighted(category)) return false;
    if (rowIndex === -1) return activeScheduleRanges.length === 5;
    return activeScheduleRanges.includes(AGE_RANGE_ENUMS[rowIndex]);
  };

  const handleCategoryHeaderClick = (category: CategoryKey): void => {
    const subCategoryIds = getSubCategoryIdsForCategory(
      category,
      adjustmentSubCategoryIds,
      idealSubCategoryIds
    );
    updateSearchParamsArray(
      TicketsRefinementKeysE.SubCategory,
      subCategoryIds ? [subCategoryIds.join(',')] : null
    );
  };

  const handleCellClick = (category: CategoryKey, rowIndex: number): void => {
    const subCategoryIds = getSubCategoryIdsForCategory(
      category,
      adjustmentSubCategoryIds,
      idealSubCategoryIds
    );
    const scheduleVal = searchParams.get(TicketsRefinementKeysE.Schedule);
    const currentSchedule = scheduleVal ? scheduleVal.split(',').map((v): string => v.trim()) : [];
    const isSameCategory =
      activeCategory === category || (activeCategory === null && category === 'schedule');

    let nextSchedule: string[];
    if (rowIndex === -1) {
      const allSelected = currentSchedule.length === 5;
      nextSchedule = allSelected ? [] : [...AGE_RANGE_ENUMS];
    } else if (isSameCategory) {
      const enumVal = AGE_RANGE_ENUMS[rowIndex];
      const idx = currentSchedule.indexOf(enumVal);
      nextSchedule =
        idx >= 0
          ? currentSchedule.filter((_, i): boolean => i !== idx)
          : [...currentSchedule, enumVal];
    } else {
      nextSchedule = rowIndex === -1 ? [...AGE_RANGE_ENUMS] : [AGE_RANGE_ENUMS[rowIndex]];
    }

    if (nextSchedule.length === 0) {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
      updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
    } else {
      updateSearchParamsArray(
        TicketsRefinementKeysE.SubCategory,
        subCategoryIds ? [subCategoryIds.join(',')] : null
      );
      updateSearchParamsArray(TicketsRefinementKeysE.Schedule, nextSchedule);
    }
  };

  const scheduleDateStr =
    scheduleReport?.lastUpdatedDateTime &&
    format(new Date(scheduleReport.lastUpdatedDateTime), 'MMMM dd, yyyy');

  const isCellHovered = (category: CategoryKey, rowIndex: number): boolean =>
    hoveredCell !== null && hoveredCell.category === category && hoveredCell.rowIndex === rowIndex;

  const getCellSx = (category: CategoryKey, rowIndex: number) => (theme: Theme) => {
    let bg = 'transparent';
    if (isRangeCellHighlighted(category, rowIndex)) {
      bg = alpha(theme.palette.primary.main, 0.2);
    } else if (isCategoryHighlighted(category)) {
      bg = alpha(theme.palette.primary.main, 0.06);
    }
    if (isCellHovered(category, rowIndex)) {
      bg = alpha(theme.palette.primary.main, 0.12);
    }
    return {
      cursor: 'pointer',
      backgroundColor: bg,
    };
  };

  const cellMouseHandlers = (
    category: CategoryKey,
    rowIndex: number
  ): {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  } => ({
    onMouseEnter: (): void => setHoveredCell({ category, rowIndex }),
    onMouseLeave: (): void => setHoveredCell(null),
  });

  const getHeaderSx = (category: CategoryKey) => (theme: Theme) => ({
    cursor: 'pointer',
    backgroundColor: isCategoryHighlighted(category)
      ? alpha(theme.palette.primary.main, 0.12)
      : 'transparent',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  });

  if (isLoadingScheduleReport || !scheduleReport) {
    return (
      <ScheduleCardCompactWrapStyled>
        {isMobile ? (
          <Accordion
            expanded={isExpanded}
            onChange={(_e, expanded): void => setIsExpanded(expanded)}
            disableGutters
            sx={ACCORDION_SX}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
              sx={ACCORDION_SUMMARY_SX}
            >
              <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>
                Schedule as of {scheduleDateStr ?? '—'}
              </MuiTypography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
              <InfoCard fullWidth={false} compact>
                <ScheduleCompactInnerStyled $compact={true}>
                  <CardLoaderWrapStyled>
                    <CardLoader />
                  </CardLoaderWrapStyled>
                </ScheduleCompactInnerStyled>
              </InfoCard>
            </AccordionDetails>
          </Accordion>
        ) : (
          <InfoCard fullWidth={false} compact>
            <ScheduleCompactInnerStyled $compact={true}>
              <ScheduleTitleStyled variant="body2" weight="normal" $compact={true}>
                Schedule as of {scheduleDateStr ?? '—'}
              </ScheduleTitleStyled>
              <CardLoaderWrapStyled>
                <CardLoader />
              </CardLoaderWrapStyled>
            </ScheduleCompactInnerStyled>
          </InfoCard>
        )}
      </ScheduleCardCompactWrapStyled>
    );
  }

  const tableContent = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        minWidth: 560,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Table
        size="small"
        aria-label="Combined schedule by category and range"
        sx={{
          '& .MuiTableCell-root': { py: 0.5, px: 0.45, fontSize: 10.5 },
          '& .MuiTableHead .MuiTableCell-root': { py: 0.4, fontSize: 9.5 },
          '& .MuiTableRow-root td, & .MuiTableRow-root th': { borderBottom: 'none' },
          '& .MuiTableCell-root:first-of-type': { maxWidth: 36, paddingRight: 0.5 },
          '& .MuiTableCell-root:nth-of-type(n+2):nth-of-type(-n+7)': { maxWidth: 62 },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: (theme: Theme) => theme.palette.background?.default ?? '#F8F9FA',
            }}
          >
            <TableCell component="th" scope="col" sx={{ pr: 0.5 }} />
            <TableCell
              colSpan={2}
              align="center"
              onClick={(): void => handleCategoryHeaderClick('schedule')}
              sx={getHeaderSx('schedule')}
            >
              {CATEGORY_LABELS.schedule}
            </TableCell>
            <TableCell
              colSpan={2}
              align="center"
              onClick={(): void => handleCategoryHeaderClick('adjustments')}
              sx={getHeaderSx('adjustments')}
            >
              {CATEGORY_LABELS.adjustments}
            </TableCell>
            <TableCell
              colSpan={2}
              align="center"
              onClick={(): void => handleCategoryHeaderClick('ideal')}
              sx={getHeaderSx('ideal')}
            >
              {CATEGORY_LABELS.ideal}
            </TableCell>
          </TableRow>
          <TableRow
            sx={{
              backgroundColor: (theme: Theme) => theme.palette.background?.default ?? '#F8F9FA',
            }}
          >
            <TableCell sx={{ fontWeight: 700, maxWidth: 36, pr: 0.5 }}>Range</TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('schedule')}
            >
              Amount
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('schedule')}
            >
              Tickets
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('adjustments')}
            >
              Amount
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('adjustments')}
            >
              Tickets
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('ideal')}
            >
              Amount
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: 10 }}
              onClick={(): void => handleCategoryHeaderClick('ideal')}
            >
              Tickets
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, pr: 0.5, maxWidth: 36 }}>All</TableCell>
            <Tooltip
              title={renderBreakdownTooltip(scheduleReport?.totalDataBySubCategories ?? [])}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('schedule', -1)}
                sx={(theme): Record<string, unknown> => ({
                  ...getCellSx('schedule', -1)(theme),
                  color: getAmountColor(scheduleReport?.totalAmount ?? 0),
                })}
                {...cellMouseHandlers('schedule', -1)}
              >
                {formatAmount(scheduleReport?.totalAmount ?? 0)}
              </TableCell>
            </Tooltip>
            <Tooltip
              title={renderBreakdownTooltip(scheduleReport?.totalDataBySubCategories ?? [])}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('schedule', -1)}
                sx={getCellSx('schedule', -1)}
                {...cellMouseHandlers('schedule', -1)}
              >
                {scheduleReport?.totalCount ?? 0}
              </TableCell>
            </Tooltip>
            <Tooltip
              title={renderBreakdownTooltip(scheduleReport?.totalAdjustedDataBySubCategories ?? [])}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('adjustments', -1)}
                sx={(theme): Record<string, unknown> => ({
                  ...getCellSx('adjustments', -1)(theme),
                  color: getAmountColor(scheduleReport?.totalAdjustedAmount ?? 0),
                })}
                {...cellMouseHandlers('adjustments', -1)}
              >
                {formatAmount(scheduleReport?.totalAdjustedAmount ?? 0)}
              </TableCell>
            </Tooltip>
            <Tooltip
              title={renderBreakdownTooltip(scheduleReport?.totalAdjustedDataBySubCategories ?? [])}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('adjustments', -1)}
                sx={getCellSx('adjustments', -1)}
                {...cellMouseHandlers('adjustments', -1)}
              >
                {scheduleReport?.totalAdjustedCount ?? 0}
              </TableCell>
            </Tooltip>
            <Tooltip
              title={renderBreakdownTooltip(idealTotals.subCategories)}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('ideal', -1)}
                sx={(theme): Record<string, unknown> => ({
                  ...getCellSx('ideal', -1)(theme),
                  color: getAmountColor(idealTotals.amount),
                })}
                {...cellMouseHandlers('ideal', -1)}
              >
                {formatAmount(idealTotals.amount)}
              </TableCell>
            </Tooltip>
            <Tooltip
              title={renderBreakdownTooltip(idealTotals.subCategories)}
              arrow
              placement="right"
            >
              <TableCell
                align="right"
                onClick={(): void => handleCellClick('ideal', -1)}
                sx={getCellSx('ideal', -1)}
                {...cellMouseHandlers('ideal', -1)}
              >
                {idealTotals.count}
              </TableCell>
            </Tooltip>
          </TableRow>
          {scheduleReport?.data.map((report, index: number) => {
            const idealRow = idealDataByAgeRange[index];
            return (
              <TableRow key={`row-${index}`}>
                <TableCell sx={{ fontWeight: 600, pr: 0.5 }}>
                  {report.ageRange.from}
                  {report.ageRange.to != null ? `-${report.ageRange.to}` : '+'}
                </TableCell>
                <Tooltip
                  title={renderBreakdownTooltip(report.dataBySubCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('schedule', index)}
                    sx={(theme): Record<string, unknown> => ({
                      ...getCellSx('schedule', index)(theme),
                      color: getAmountColor(report.amount ?? 0),
                    })}
                    {...cellMouseHandlers('schedule', index)}
                  >
                    {formatAmount(report.amount ?? 0)}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={renderBreakdownTooltip(report.dataBySubCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('schedule', index)}
                    sx={getCellSx('schedule', index)}
                    {...cellMouseHandlers('schedule', index)}
                  >
                    {report.count ?? 0}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={renderBreakdownTooltip(report.adjustedDataBySubCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('adjustments', index)}
                    sx={(theme): Record<string, unknown> => ({
                      ...getCellSx('adjustments', index)(theme),
                      color: getAmountColor(report.adjustedAmount ?? 0),
                    })}
                    {...cellMouseHandlers('adjustments', index)}
                  >
                    {formatAmount(report.adjustedAmount ?? 0)}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={renderBreakdownTooltip(report.adjustedDataBySubCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('adjustments', index)}
                    sx={getCellSx('adjustments', index)}
                    {...cellMouseHandlers('adjustments', index)}
                  >
                    {report.adjustedCount ?? 0}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={renderBreakdownTooltip(idealRow?.subCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('ideal', index)}
                    sx={(theme): Record<string, unknown> => ({
                      ...getCellSx('ideal', index)(theme),
                      color: getAmountColor(idealRow?.amount ?? 0),
                    })}
                    {...cellMouseHandlers('ideal', index)}
                  >
                    {formatAmount(idealRow?.amount ?? 0)}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={renderBreakdownTooltip(idealRow?.subCategories ?? [])}
                  arrow
                  placement="right"
                >
                  <TableCell
                    align="right"
                    onClick={(): void => handleCellClick('ideal', index)}
                    sx={getCellSx('ideal', index)}
                    {...cellMouseHandlers('ideal', index)}
                  >
                    {idealRow?.count ?? 0}
                  </TableCell>
                </Tooltip>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <ScheduleCardCompactWrapStyled>
      {isMobile ? (
        <Accordion
          expanded={isExpanded}
          onChange={(_e, expanded): void => setIsExpanded(expanded)}
          disableGutters
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>
              Schedule as of {scheduleDateStr ?? '—'}
            </MuiTypography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
            <InfoCard fullWidth={false} compact>
              <ScheduleCompactInnerStyled $compact={true}>
                {tableContent}
              </ScheduleCompactInnerStyled>
            </InfoCard>
          </AccordionDetails>
        </Accordion>
      ) : (
        <InfoCard fullWidth={false} compact>
          <ScheduleCompactInnerStyled $compact={true}>
            <Box sx={{ mb: 0.125, '& > *': { marginBottom: 0 } }}>
              <ScheduleTitleStyled variant="body2" weight="normal" $compact={true}>
                Schedule as of {scheduleDateStr ?? '—'}
              </ScheduleTitleStyled>
            </Box>
            {tableContent}
          </ScheduleCompactInnerStyled>
        </InfoCard>
      )}
    </ScheduleCardCompactWrapStyled>
  );
};

export default CombinedScheduleCards;
